import { useEffect, useMemo, useRef, type RefObject } from "react"
import { Formik, getIn, useFormikContext } from "formik"
import { CharField, SingleSelectField } from "@inmagik/magik-ui"
import * as yup from "yup"
import type {
  BlockBase,
  Media,
  MediaItem,
  ImageGridTextBlock,
  CaptionMode,
  MediaFit,
  TextPosition,
} from "../../../../types"
import { MediaThumb } from "../Previews"
import {
  captionModeOptions,
  mediaFitOptions,
  gridRowsOptions,
  gridColsOptions,
  textPositionOptions,
  mediaPositionOptions,
} from "../../options"
import { AutoValidate } from "./Autovalidate"
import { WysiwygField } from "../../../../components/WysiwygField"
import { MediaSelectionModal } from "../MediaSelectionModal"

function normalize(items: MediaItem[] | undefined): MediaItem[] {
  const base: MediaItem[] = Array.isArray(items) ? items : []
  return base.map((it) => ({
    id: it.id,
    title: it.title ?? "",
    thumbnail: it.thumbnail ?? "",
    media_file: it.media_file ?? "",
    caption: it.caption ?? "",
    fit: it.fit ?? "fit",
    position: it.position ?? "center center",
  }))
}

function GridPickerTable() {
  const { values, setFieldValue } = useFormikContext<{ items: MediaItem[]; rows: number; cols: number }>()

  const items: MediaItem[] = useMemo(() => normalize(values.items), [values.items])
  const selectedIds = useMemo(() => items.map((x) => x.id).filter(Boolean) as number[], [items])
  const isSelected = (id: number) => selectedIds.includes(id)
  const maxItems = values.rows * values.cols

  const toggle = (doc: Media) => {
    const id = doc.id as number
    if (isSelected(id)) {
      setFieldValue(
        "items",
        items.filter((x) => x.id !== id),
      )
      return
    }
    if (items.length >= maxItems) return

    setFieldValue("items", [
      ...items,
      {
        id,
        title: doc.title ?? "",
        thumbnail: String(doc.thumbnail ?? doc.media_file ?? ""),
        media_file: String(doc.media_file ?? ""),
        caption: "",
        fit: "fit" as MediaFit,
        position: "center center",
      },
    ])
  }

  return (
    <MediaSelectionModal
      buttonLabel="Seleziona immagini"
      title="Seleziona immagini griglia"
      selectionSummary={`${selectedIds.length} / ${maxItems} immagini selezionate.`}
      renderSelectionCell={(doc) => {
        const checked = doc.id ? isSelected(doc.id) : false
        const disabled = !!doc.id && !checked && items.length >= maxItems

        return <input type="checkbox" checked={checked} onChange={() => toggle(doc)} disabled={disabled} />
      }}
      onCreateAndSelect={toggle}
    />
  )
}

export function ImageGridTextBlockForm({
  block,
  saveBlock,
  setBlockFormValid,
  submitRef,
}: {
  block: BlockBase
  saveBlock: (b: BlockBase) => void
  setBlockFormValid: (v: boolean) => void
  submitRef: RefObject<HTMLButtonElement | null>
}) {
  const schema = yup.object().shape({
    slideTitle: yup.string().required("Il titolo della slide è obbligatorio"),
    rows: yup.number().min(1).max(4).required("Seleziona il numero di righe"),
    cols: yup.number().min(1).max(4).required("Seleziona il numero di colonne"),
    items: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.number().required(),
          fit: yup.string().oneOf(["fit", "cover"]).required(),
          caption: yup.string().nullable(),
        }),
      )
      .required("Seleziona le immagini")
      .test("exact-grid-size", function (value) {
        const { rows, cols } = this.parent
        const expected = (rows ?? 0) * (cols ?? 0)
        const actual = Array.isArray(value) ? value.length : 0

        if (actual !== expected) {
          return this.createError({
            message: `Devi selezionare esattamente ${expected} immagini (${rows} × ${cols})`,
          })
        }

        return true
      }),
    captionMode: yup.string().oneOf(["single", "perItem"]).required(),
    caption: yup.string().nullable(),
    content: yup.string().required("Il testo è obbligatorio"),
    textPosition: yup.string().oneOf(["left", "right"]).required("Seleziona la posizione del testo"),
  })

  const hiddenSubmitRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!submitRef?.current) return
    submitRef.current.onclick = () => hiddenSubmitRef.current?.click()
  }, [submitRef])

  const initialValues: ImageGridTextBlock = {
    uuid: block.uuid,
    blockType: "ImageGridText",
    slideTitle: (block as ImageGridTextBlock).slideTitle ?? "",
    rows: (block as ImageGridTextBlock).rows ?? 2,
    cols: (block as ImageGridTextBlock).cols ?? 2,
    items: normalize((block as ImageGridTextBlock).items),
    captionMode: (block as ImageGridTextBlock).captionMode ?? "single",
    caption: (block as ImageGridTextBlock).caption ?? "",
    content: (block as ImageGridTextBlock).content ?? "",
    textPosition: (block as ImageGridTextBlock).textPosition ?? "left",
  }

  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={(values) => saveBlock(values)}
    >
      {({ handleSubmit, values, errors, setFieldValue }) => {
        const items: MediaItem[] = normalize(values.items)
        const maxItems = values.rows * values.cols

        const setItem = (idx: number, patch: Partial<MediaItem>) => {
          const next = normalize(values.items)
          next[idx] = { ...next[idx], ...patch }
          setFieldValue("items", next)
        }

        const removeItem = (idx: number) => {
          const next = normalize(values.items).filter((_, i) => i !== idx)
          setFieldValue("items", next)
        }

        const moveItem = (from: number, to: number) => {
          const next = normalize(values.items)
          if (to < 0 || to >= next.length) return

          const [moved] = next.splice(from, 1)
          next.splice(to, 0, moved)

          setFieldValue("items", next)
        }

        return (
          <form onSubmit={handleSubmit}>
            <CharField
              label="Titolo slide"
              layout="horizontal"
              value={values.slideTitle}
              onChange={(v) => setFieldValue("slideTitle", v)}
              required
              isValid={!getIn(errors, "slideTitle")}
              errorMessage={getIn(errors, "slideTitle")}
              className="mb-3"
            />

            <div className="mb-3">
              <div className="mb-2">
                <SingleSelectField
                  label="Numero righe"
                  layout="horizontal"
                  options={gridRowsOptions}
                  value={gridRowsOptions.find((o) => o.value === values.rows)}
                  onChange={(opt) => {
                    if (opt) {
                      const newRows = opt.value as number
                      const newMax = newRows * values.cols
                      setFieldValue("rows", newRows)
                      if (items.length > newMax) {
                        setFieldValue("items", items.slice(0, newMax))
                      }
                    }
                  }}
                  errorMessage={getIn(errors, "rows")}
                />
              </div>

              <div className="mb-2">
                <SingleSelectField
                  label="Numero colonne"
                  layout="horizontal"
                  options={gridColsOptions}
                  value={gridColsOptions.find((o) => o.value === values.cols)}
                  onChange={(opt) => {
                    if (opt) {
                      const newCols = opt.value as number
                      const newMax = values.rows * newCols
                      setFieldValue("cols", newCols)
                      if (items.length > newMax) {
                        setFieldValue("items", items.slice(0, newMax))
                      }
                    }
                  }}
                  errorMessage={getIn(errors, "cols")}
                />
              </div>
            </div>

            <div className="mb-3 text-muted text-center" style={{ fontSize: 13 }}>
              Massimo immagini selezionabili: {maxItems} ({values.rows} × {values.cols})
            </div>

            <div className="mb-3 d-flex align-items-center flex-column gap-2 mb-3 w-100">
              <GridPickerTable />
            </div>

            <div className="mb-3">
              <SingleSelectField
                label="Didascalia"
                layout="horizontal"
                options={captionModeOptions}
                value={captionModeOptions.find((o) => o.value === values.captionMode)}
                onChange={(opt) => opt && setFieldValue("captionMode", opt.value as CaptionMode)}
                errorMessage={getIn(errors, "captionMode")}
              />
            </div>

            {values.captionMode === "single" && (
              <WysiwygField
                label="Didascalia unica"
                layout="horizontal"
                value={values.caption || ""}
                onChange={(v) => setFieldValue("caption", v)}
                className="mb-3"
                placeholder="(opzionale)"
                rows={6}
              />
            )}

            <WysiwygField
              label="Testo"
              layout="horizontal"
              value={values.content}
              onChange={(v) => setFieldValue("content", v)}
              required
              isValid={!getIn(errors, "content")}
              errorMessage={getIn(errors, "content")}
              className="mb-3"
            />

            <div className="mb-3">
              <SingleSelectField
                label="Posizione testo"
                layout="horizontal"
                options={textPositionOptions}
                value={textPositionOptions.find((o) => o.value === values.textPosition)}
                onChange={(opt) => opt && setFieldValue("textPosition", opt.value as TextPosition)}
                errorMessage={getIn(errors, "textPosition")}
              />
            </div>

            <div className="mb-3 d-flex flex-column gap-3">
              {items.map((it, idx) => (
                <div
                  key={it.id ?? idx}
                  style={{
                    padding: 12,
                    border: "1px solid rgba(0,0,0,.08)",
                    borderRadius: 10,
                    background: "#f2f2f2",
                    boxShadow: "0 1px 3px rgba(0,0,0,.1)",
                  }}
                >
                  <div className="w-100">
                    <div className="d-flex align-items-center flex-column justify-content-center gap-3 w-100 mb-3">
                      <MediaThumb
                        thumbnail={it.thumbnail}
                        title={it.title}
                        fit={it.fit}
                        width={250}
                        height={170}
                        position={it.position}
                      />

                      <div className="w-100 d-flex justify-content-center align-items-center flex-wrap gap-2">
                        <div style={{ fontSize: 13 }}>
                          <strong>Ordine:</strong> {idx + 1}{" "}
                          <span className="text-muted">|</span> <strong>ID:</strong>{" "}
                          {it.id ?? <span className="text-muted">—</span>}
                          {it.title ? <span className="text-muted"> — {it.title}</span> : null}
                        </div>

                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => moveItem(idx, idx - 1)}
                            disabled={idx === 0}
                          >
                            Su
                          </button>

                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => moveItem(idx, idx + 1)}
                            disabled={idx === items.length - 1}
                          >
                            Giù
                          </button>

                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => removeItem(idx)}
                          >
                            Rimuovi
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="mb-2">
                      <SingleSelectField
                        label="Fit"
                        layout="horizontal"
                        options={mediaFitOptions}
                        value={mediaFitOptions.find((o) => o.value === (it.fit ?? "fit"))}
                        onChange={(opt) => opt && setItem(idx, { fit: opt.value as MediaFit })}
                      />
                    </div>

                    {it.fit === "cover" && (
                      <div className="mb-2">
                        <SingleSelectField
                          label="Posizione immagine"
                          layout="horizontal"
                          options={mediaPositionOptions}
                          value={mediaPositionOptions.find((o) => o.value === it.position)}
                          onChange={(opt) => opt && setItem(idx, { position: opt.value })}
                        />
                      </div>
                    )}

                    {values.captionMode === "perItem" && (
                      <WysiwygField
                        label="Didascalia"
                        layout="horizontal"
                        value={it.caption || ""}
                        onChange={(v) => setItem(idx, { caption: v })}
                        className="mb-2"
                        rows={6}
                        placeholder="(opzionale)"
                      />
                    )}
                  </div>
                </div>
              ))}

              {!items.length && <div className="text-muted">Nessuna immagine selezionata</div>}
            </div>

            {getIn(errors, "items") ? (
              <div className="text-danger mb-3" style={{ fontSize: 13 }}>
                {typeof getIn(errors, "items") === "string"
                  ? String(getIn(errors, "items"))
                  : "Controlla le immagini selezionate"}
              </div>
            ) : null}

            <button ref={hiddenSubmitRef} type="submit" style={{ display: "none" }} />
            <AutoValidate setBlockFormValid={setBlockFormValid} />
          </form>
        )
      }}
    </Formik>
  )
}